import React from 'react';

import { useGetWalmartFormItemError } from 'pages/branded-products/components/post-to-walmart-krogger/hooks';
import { filterOptionHandler } from 'pages/branded-products/components/post-to-walmart-krogger/post-product-form/utils';
import { PostField, PostFieldList } from './component';
import {
  GroupElementWrapper,
  HeaderGroupElement,
} from 'pages/product-full-view/shared/components';
import { Row } from 'antd';

const WalmartFields = React.memo((props) => {
  const {
    type,
    childProperties,
    items,
    title,
    fullKey,
    fieldKey,
    className,
    format,
    minItems,
    maxItems,
    enums,
    lowerLimit,
    upperLimit,
    description,
    isRequiredProperty,
    fieldListItemProps,
    formItemStyle,
    labelCol,
    wrapperCol,
    productId,
    showServerError = true,
    errorKey,
    name,
    filterOption,
    isRivirMappingField,
    rivirMappingField,
    multipleOf,
  } = props;

  const formItemServerError = useGetWalmartFormItemError({
    productId,
    errorKey: errorKey || fullKey,
  });

  // Common props shared across all PostField components
  const commonFieldProps = {
    fieldKey,
    fullKey,
    label: title,
    labelCol,
    wrapperCol,
    fieldListItemProps,
    formItemStyle,
    className,
    description,
    isRequiredProperty,
    serverError: formItemServerError,
    showServerError,
    name,
    isRivirMappingField,
    rivirMappingField,
    step: multipleOf,
  };

  // Handle object type - recursive rendering
  if (type === 'object') {
    return (
      <Row
        style={{
          position: 'relative',
        }}
        wrap={false}
      >
        <GroupElementWrapper
          flex='auto'
          style={{ margin: 24, marginBottom: 6 }}
          className='product-detail__group-element'
        >
          <HeaderGroupElement header={title} />

          {childProperties?.map((fieldItem) => {
            const { key, enums, ...restFieldItemProps } = fieldItem;
            return (
              <WalmartFields
                key={fieldItem?.fullKey}
                {...restFieldItemProps}
                fieldKey={key}
                productId={productId}
                showServerError={showServerError}
                filterOption={filterOptionHandler(enums, fullKey)}
                enums={enums}
              />
            );
          })}
        </GroupElementWrapper>
      </Row>
    );
  }

  // Handle array type
  if (type === 'array') {
    // Multi-select dropdown (array of strings with enums)
    if (items?.type === 'string' && items?.enums?.length > 0) {
      return (
        <PostField
          {...commonFieldProps}
          type='multi-select'
          enums={items.enums}
          minItems={minItems}
          ruleType='list'
          filterOption={filterOptionHandler(items.enums, fullKey)}
        />
      );
    }

    // Multiple image URLs
    if (items?.format === 'uri') {
      return (
        <PostField
          {...commonFieldProps}
          type='product-image-url-multiple'
          minItems={minItems}
          ruleType='list'
          productId={productId}
        />
      );
    }

    // Complex array items (nested objects/arrays)
    return (
      <PostFieldList
        fullKey={fullKey}
        fieldName={title}
        label={title}
        items={items}
        minItems={minItems}
        isRequiredProperty={isRequiredProperty}
        productId={productId}
        serverError={formItemServerError}
        description={description}
        filterOption={filterOption}
        isRivirMappingField={isRivirMappingField}
        rivirMappingField={rivirMappingField}
      />
    );
  }

  // Handle string type
  if (type === 'string') {
    // Single image URL
    if (format === 'uri') {
      return (
        <PostField
          {...commonFieldProps}
          type='product-image-url'
          productId={productId}
        />
      );
    }

    // DateTime picker
    if (format === 'date-time') {
      return <PostField {...commonFieldProps} type='dateTime' />;
    }

    // Single select dropdown
    if (enums?.length > 0) {
      return (
        <PostField
          {...commonFieldProps}
          type='select'
          enums={enums}
          filterOption={filterOptionHandler(enums, fullKey)}
        />
      );
    }

    // Default text input
    return (
      <PostField
        {...commonFieldProps}
        type='string'
        minLength={lowerLimit}
        maxLength={upperLimit}
      />
    );
  }

  // Handle number type
  if (type === 'number') {
    return (
      <PostField
        {...commonFieldProps}
        type='number'
        min={lowerLimit}
        max={upperLimit}
      />
    );
  }

  // Handle integer type
  if (type === 'integer') {
    return <PostField {...commonFieldProps} type='integer' />;
  }

  return null;
});

WalmartFields.displayName = 'WalmartFields';

export { WalmartFields };
